import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import HeaderOne from '../components/HeaderOne';
import FooterOne from '../components/FooterOne';

import Preloader from "../helper/Preloader";
import ColorInit from "../helper/ColorInit";
import ScrollToTop from "react-scroll-to-top";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};
const shortOrderId = (razorpay_id, id) =>
  razorpay_id ? razorpay_id.slice(-10).toUpperCase() : `ORD-${String(id).padStart(6, '0')}`;
const initials = (name) => (name || '?')[0].toUpperCase();

const STATUS_META = {
  processing: { label: 'In Progress', dotColor: '#F57C00', badgeBg: '#FFF3E0', badgeColor: '#E65100' },
  delivered:  { label: 'Delivered',   dotColor: '#3B6D11', badgeBg: '#EAF3DE', badgeColor: '#3B6D11' },
  confirmed:  { label: 'Confirmed',   dotColor: '#185FA5', badgeBg: '#E6F1FB', badgeColor: '#0C447C' },
  shipped:    { label: 'Shipped',     dotColor: '#7b1fa2', badgeBg: '#f3e5f5', badgeColor: '#6a1b9a' },
  pending:    { label: 'Pending',     dotColor: '#888',    badgeBg: '#f3f4f6', badgeColor: '#444'    },
  cancelled:  { label: 'Cancelled',  dotColor: '#E53935', badgeBg: '#FFEBEE', badgeColor: '#b71c1c' },
};

const FILTERS = [
  { key: 'all',        label: 'All Orders'  },
  { key: 'processing', label: 'In Progress' },
  { key: 'delivered',  label: 'Delivered'   },
  { key: 'confirmed',  label: 'Confirmed'   },
  { key: 'cancelled',  label: 'Cancelled'   },
];

const STEPS       = ['Order Placed', 'Processing', 'Shipped', 'Out for Delivery', 'Delivered'];
const STATUS_STEP = { pending: 0, processing: 1, confirmed: 1, shipped: 2, out_for_delivery: 3, delivered: 4 };

const getCustomerId = () => {
  try { return JSON.parse(localStorage.getItem('customer'))?.id || null; }
  catch { return null; }
};

/* ─────────────────────────── StatusBadge ─────────────────────────── */
const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '5px 12px', borderRadius: 50,
      background: meta.badgeBg, color: meta.badgeColor,
      fontSize: 12, fontWeight: 700,
      border: `0.5px solid ${meta.dotColor}44`,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.dotColor, display: 'inline-block' }} />
      {meta.label}
    </span>
  );
};

/* ─────────────────────────── OrderDetail (inline panel) ─────────────────────────── */
const OrderDetail = ({ order, onClose }) => {
  const detailRef = useRef(null);
  const items = order.OrderItems || [];
  const step  = STATUS_STEP[order.status] ?? 0;

  /* scroll detail into view on open */
  useEffect(() => {
    if (detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [order]);

  return (
    
    <div ref={detailRef} style={S.detailWrap}>

      {/* Detail header */}
      <div style={S.detailHeader}>
        <div>
          <div style={S.detailLabel}>Order Details</div>
          <div style={S.detailId}>{shortOrderId(order.razorpay_order_id, order.id)}</div>
        </div>
        <button style={S.closeBtn} onClick={onClose}>✕ Close</button>
      </div>

      {/* Stepper */}
      <div style={S.detailSection}>
        <div style={S.cardTitle}>Order Status</div>
        <div style={{ paddingBottom: 4 }}>
          <div style={S.stepsTrack}>
            <div style={{ ...S.stepsFill, width: `${(step / (STEPS.length - 1)) * 100}%` }} />
          </div>
          <div style={S.stepsRow}>
            {STEPS.map((s, i) => {
              const done = i < step, active = i === step;
              return (
                <div key={i} style={S.stepCol}>
                  <div style={{ ...S.stepDot, ...(done ? S.stepDotDone : {}), ...(active ? S.stepDotActive : {}) }}>
                    {done ? '✓' : active ? '●' : ''}
                  </div>
                  <span style={{ ...S.stepLbl, ...((done || active) ? { color: '#185FA5', fontWeight: 700 } : {}) }}>{s}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Items */}
      <div style={S.detailSection}>
        <div style={S.cardTitle}>Items Ordered</div>
        {items.map((item, i) => (
          <div key={i} style={S.itemRow}>
            <div style={S.itemIcon}>{initials(item.name)}</div>
            <div style={S.itemInfo}>
              <div style={S.itemName}>{item.name}</div>
              <div style={S.itemMeta}>Qty: {item.quantity || 1}</div>
            </div>
            <div style={S.itemPrice}>{fmt((item.price || 0) * (item.quantity || 1))}</div>
          </div>
        ))}
      </div>

      {/* Price Summary */}
      <div style={S.detailSection}>
        <div style={S.cardTitle}>Price Summary</div>
        <div style={S.summaryBox}>
          <div style={S.summaryRow}>
            <span style={S.sumLabel}>Subtotal</span>
            <span style={S.sumValue}>{fmt(order.total)}</span>
          </div>
          <div style={S.summaryRow}>
            <span style={S.sumLabel}>Delivery</span>
            <span style={S.freeBadge}>FREE</span>
          </div>
          <hr style={S.dividerDash} />
          <div style={S.summaryRow}>
            <span style={S.sumTotalLabel}>Total Paid</span>
            <span style={S.sumTotalValue}>{fmt(order.total)}</span>
          </div>
        </div>
      </div>

      {/* Payment Info */}
      <div style={{ ...S.detailSection, paddingBottom: 8 }}>
        <div style={S.cardTitle}>Payment Info</div>
        <div style={S.infoGrid}>
          {order.razorpay_order_id && (
            <div style={S.infoCell}>
              <div style={S.infoLabel}>Razorpay ID</div>
              <div style={{ ...S.infoValue, fontFamily: 'monospace', fontSize: 11, color: '#185FA5' }}>
                {order.razorpay_order_id.slice(-12).toUpperCase()}
              </div>
            </div>
          )}
          <div style={S.infoCell}>
            <div style={S.infoLabel}>Order Date</div>
            <div style={S.infoValue}>{fmtDate(order.createdAt)}</div>
          </div>
          <div style={S.infoCell}>
            <div style={S.infoLabel}>Amount Paid</div>
            <div style={{ ...S.infoValue, color: '#185FA5', fontWeight: 700 }}>{fmt(order.total)}</div>
          </div>
          <div style={S.infoCell}>
            <div style={S.infoLabel}>Status</div>
            <StatusBadge status={order.status} />
          </div>
        </div>
      </div>

      {/* Close button at bottom */}
      <div style={{ padding: '16px 22px 22px', textAlign: 'center' }}>
        <button onClick={onClose} style={S.closeBtnBottom}>← Back to Orders</button>
      </div>
    </div>
  );
};

/* ─────────────────────────── OrderCard ─────────────────────────── */
const OrderCard = ({ order, isSelected, onClick, idx }) => {
  const items       = order.OrderItems || [];
  const first       = items[0] || {};
  const previewNames = items.slice(0, 2).map(i => i.name).join(' | ');
  const extraCount  = items.length - 2;
  const step        = STATUS_STEP[order.status] ?? 0;
  const meta        = STATUS_META[order.status] || STATUS_META.pending;

  return (
    <div
      className="ty-order-card"
      style={{
        ...S.card,
        animationDelay: `${idx * 0.07}s`,
        ...(isSelected ? S.cardSelected : {}),
      }}
      onClick={onClick}
    >
      {/* Status row */}
      <div style={S.cardTop}>
        <div style={S.statusRow}>
          <StatusBadge status={order.status} />
          <span style={S.dateTxt}>{fmtDate(order.createdAt)}</span>
        </div>
      </div>

      <hr style={S.dividerSolid} />

      {/* Body */}
      <div style={S.cardBody}>
        <div style={S.thumb}>
          {order.image
            ? <img src={order.image} alt={first.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} onError={e => { e.target.style.display = 'none'; }} />
            : <span style={{ fontSize: 20, fontWeight: 800, color: '#185FA5' }}>{initials(first.name)}</span>
          }
          {items.length > 1 && <span style={S.thumbCount}>+{items.length - 1}</span>}
        </div>

        <div style={S.info}>
          <div style={S.orderId}>{shortOrderId(order.razorpay_order_id, order.id)}</div>
          <div style={S.itemsTxt}>
            {previewNames}
            {extraCount > 0 && (
              <span style={S.moreItems}> &amp; {extraCount} more item{extraCount > 1 ? 's' : ''}</span>
            )}
          </div>
          <div style={S.price}>{fmt(order.total)}</div>
        </div>

        <span style={{ ...S.chevron, transform: isSelected ? 'rotate(90deg)' : 'none', transition: 'transform .2s' }}>›</span>
      </div>

      {/* Mini stepper */}
      <div style={S.miniStepWrap}>
        <div style={S.miniTrack}>
          <div style={{ ...S.miniFill, width: `${(step / (STEPS.length - 1)) * 100}%`, background: meta.dotColor }} />
          {STEPS.map((_, i) => (
            <div key={i} style={{ ...S.miniDot, ...(i <= step ? { background: meta.dotColor, borderColor: meta.dotColor } : {}) }} />
          ))}
        </div>
        <div style={S.miniLabels}>
          {STEPS.map((s, i) => (
            <span key={i} style={{ ...S.miniLbl, ...(i === step ? { color: meta.dotColor, fontWeight: 700 } : {}) }}>{s}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────── Main ─────────────────────────── */
const MyOrders = () => {
  const location               = useLocation();
  const [orders,   setOrders]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [filter,   setFilter]   = useState('all');
  const [selected, setSelected] = useState(null); // order object

  const fetchOrders = useCallback(async () => {
    const cid = getCustomerId();
    if (!cid) { setLoading(false); setError('not_logged_in'); return; }
    setLoading(true); setError(null);
    const delay = location.state?.fromThankYou ? 800 : 0;
    setTimeout(async () => {
      try {
        const res  = await fetch(`${API_BASE_URL}/orders/customer/${cid}`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        setOrders(Array.isArray(data.orders) ? data.orders : []);
      } catch { setError('fetch_failed'); }
      finally { setLoading(false); }
    }, delay);
  }, [location.key]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleCardClick = (order) => {
    /* toggle: clicking same card again closes detail */
    setSelected(prev => (prev?.id === order.id ? null : order));
  };

  const visible = orders.filter(o => filter === 'all' || o.status === filter);

  /* ── Loading ── */
  if (loading) return (
     <>
    <HeaderOne/>

    
    <div style={S.page}>
      <style>{CSS}</style>
      <div style={S.centerPage}>
        <div style={S.spinner} />
        <p style={S.spinnerTxt}>Loading your orders…</p>
      </div>
    </div>
     </> 
  );

  /* ── Not logged in ── */
  if (error === 'not_logged_in') return (
     <>
    <HeaderOne />
    <div style={S.page}>
      <style>{CSS}</style>
      <div style={S.centerPage}>
        <div style={S.emptyIcon}>🔒</div>
        <h3 style={S.emptyH}>Login Required</h3>
        <p style={S.emptyP}>Please log in to view your orders.</p>
        <Link to="/account" style={S.primaryBtn}>Login / Register</Link>
      </div>
    </div>
    </>
  );

  /* ── Fetch error ── */
  if (error === 'fetch_failed') return (
    <div style={S.page}>
      <style>{CSS}</style>
      <div style={S.centerPage}>
        <div style={S.emptyIcon}>⚠️</div>
        <h3 style={S.emptyH}>Something went wrong</h3>
        <p style={S.emptyP}>Could not load your orders. Please try again.</p>
        <button style={S.primaryBtn} onClick={fetchOrders}>Retry</button>
      </div>
    </div>
  );

  /* ── Main ── */
  return (
     <>
       {/* Preloader */}
      <Preloader />
      {/* ColorInit */}
      <ColorInit color={true} />

      {/* ScrollToTop */}
      <ScrollToTop smooth color='#FA6400' />

    <HeaderOne /> 
    <div style={S.page}>
      <style>{CSS}</style>

      <div style={S.wrap}>

        {/* Hero header */}
        <div style={S.hero}>
          <div>
            <h1 style={S.heroTitle}>My Orders</h1>
            <p style={S.heroSub}>{orders.length} order{orders.length !== 1 ? 's' : ''} placed</p>
          </div>
          <Link to="/" style={S.backBtn}>← Continue Shopping</Link>
        </div>

        {/* Filter tabs */}
        <div style={S.filters}>
          {FILTERS.map(f => {
            const count    = f.key === 'all' ? orders.length : orders.filter(o => o.status === f.key).length;
            const isActive = filter === f.key;
            return (
              <button
                key={f.key}
                className="ty-filter-tab"
                style={{ ...S.filterTab, ...(isActive ? S.filterTabActive : {}) }}
                onClick={() => { setFilter(f.key); setSelected(null); }}
              >
                {f.label}
                <span style={{
                  ...S.filterCount,
                  background: isActive ? 'rgba(255,255,255,.25)' : '#E6F1FB',
                  color:      isActive ? '#fff' : '#185FA5',
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Orders list — detail panel opens inline below each card */}
        {visible.length === 0 ? (
          <div style={S.emptyCard}>
            <div style={S.emptyIcon}>🛍️</div>
            <h3 style={S.emptyH}>No orders found</h3>
            <p style={S.emptyP}>
              {filter !== 'all' ? 'No orders in this category yet.' : "You haven't placed any orders yet."}
            </p>
            <Link to="/" style={S.primaryBtn}>Start Shopping</Link>
          </div>
        ) : (
          <div style={S.list}>
            {visible.map((order, idx) => (
              <React.Fragment key={order.id}>
                <OrderCard
                  order={order}
                  idx={idx}
                  isSelected={selected?.id === order.id}
                  onClick={() => handleCardClick(order)}
                />
                {/* Inline detail panel — renders right below the clicked card */}
                {selected?.id === order.id && (
                  <OrderDetail order={selected} onClose={() => setSelected(null)} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Footer */}
        <div style={S.logoBar}>
          <div style={S.logoK}>K</div>
          <span>Kalpanamart — Thank you for shopping with us!</span>
        </div>

      </div>
    </div>
     <FooterOne /> 
    
    </>
  );

};

/* ─────────────────────────── CSS ─────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:none; } }
  @keyframes slideDown { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:none; } }
  @keyframes ty-pulse {
    0%,100% { box-shadow: 0 0 0 4px rgba(245,124,0,.15); }
    50%      { box-shadow: 0 0 0 8px rgba(245,124,0,.08); }
  }
  .ty-order-card { animation: fadeUp .4s ease both; }
  .ty-order-card:hover { box-shadow: 0 6px 24px rgba(24,95,165,.15) !important; transform: translateY(-1px); transition: all .2s; cursor: pointer; }
  .ty-filter-tab:hover { border-color: #185FA5 !important; color: #185FA5 !important; }
`;

/* ─────────────────────────── Styles ─────────────────────────── */
const S = {
  page: {
    minHeight: '100vh',
    background: '#F0F6FF',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    padding: '40px 16px 60px',
  },
  wrap: { maxWidth: 640, margin: '0 auto' },

  /* Hero */
  hero: {
    background: 'linear-gradient(135deg, #185FA5 0%, #0C447C 100%)',
    borderRadius: 20, padding: '28px 32px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 12, marginBottom: 20,
    boxShadow: '0 8px 40px rgba(24,95,165,.28)',
  },
  heroTitle: { fontSize: 24, fontWeight: 800, color: '#fff', letterSpacing: '-.3px' },
  heroSub:   { fontSize: 13, color: 'rgba(255,255,255,.75)', marginTop: 3, fontWeight: 500 },
  backBtn: {
    fontSize: 13, fontWeight: 700, color: '#fff',
    textDecoration: 'none', padding: '9px 20px',
    border: '1.5px solid rgba(255,255,255,.5)', borderRadius: 50,
    whiteSpace: 'nowrap',
  },

  /* Filters */
  filters:         { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 },
  filterTab: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 50,
    border: '1.5px solid #B5D4F4', background: '#fff',
    fontSize: 13, fontWeight: 600, color: '#374151',
    cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s',
  },
  filterTabActive: { background: '#185FA5', color: '#fff', borderColor: '#185FA5' },
  filterCount:     { borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700 },

  list: { display: 'flex', flexDirection: 'column', gap: 14 },

  /* Order card */
  card: {
    background: '#fff',
    border: '0.5px solid #B5D4F4',
    borderRadius: 16, overflow: 'hidden',
    cursor: 'pointer',
    boxShadow: '0 2px 12px rgba(24,95,165,.07)',
  },
  cardSelected: {
    borderColor: '#185FA5',
    boxShadow: '0 0 0 2px #185FA533, 0 4px 20px rgba(24,95,165,.15)',
  },
  cardTop:     { padding: '14px 18px 12px' },
  statusRow:   { display: 'flex', alignItems: 'center', gap: 10 },
  dateTxt:     { fontSize: 12, color: '#888', fontWeight: 500 },
  dividerSolid:{ border: 'none', borderTop: '0.5px solid #e8f0fb', margin: '0 18px' },
  cardBody:    { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px' },

  /* Thumb */
  thumb: {
    width: 56, height: 56, borderRadius: 8,
    border: '0.5px solid #B5D4F4', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#E6F1FB', position: 'relative', overflow: 'hidden',
  },
  thumbCount: {
    position: 'absolute', bottom: 2, right: 3,
    background: 'rgba(0,0,0,.55)', color: '#fff',
    fontSize: 9, fontWeight: 700, padding: '1px 4px', borderRadius: 4,
  },

  /* Info */
  info:      { flex: 1, minWidth: 0 },
  orderId:   { fontSize: 12, fontWeight: 700, color: '#185FA5', marginBottom: 3, fontFamily: 'monospace' },
  itemsTxt:  { fontSize: 13, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.4 },
  moreItems: { color: '#F57C00', fontWeight: 600 },
  price:     { fontSize: 15, fontWeight: 800, color: '#0C447C', marginTop: 4 },
  chevron:   { fontSize: 24, color: '#185FA5', flexShrink: 0, lineHeight: 1, fontWeight: 700 },

  /* Mini stepper on card */
  miniStepWrap: { padding: '10px 18px 14px' },
  miniTrack: {
    position: 'relative', height: 4,
    background: '#E6F1FB', borderRadius: 4,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 8,
  },
  miniFill:  { position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 4, transition: 'width .6s ease' },
  miniDot:   { width: 12, height: 12, borderRadius: '50%', border: '2px solid #B5D4F4', background: '#fff', position: 'relative', zIndex: 1, flexShrink: 0, transition: 'all .3s' },
  miniLabels:{ display: 'flex', justifyContent: 'space-between' },
  miniLbl:   { fontSize: 9, color: '#aaa', fontWeight: 600, textAlign: 'center', flex: 1 },

  /* ── Inline detail panel ── */
  detailWrap: {
    background: '#fff',
    border: '2px solid #185FA5',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(24,95,165,.18)',
    animation: 'slideDown .3s ease',
  },
  detailHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '18px 22px',
    background: 'linear-gradient(135deg, #185FA5 0%, #0C447C 100%)',
  },
  detailLabel: { fontSize: 11, color: 'rgba(255,255,255,.7)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em' },
  detailId:    { fontSize: 14, fontWeight: 800, color: '#fff', marginTop: 3, fontFamily: 'monospace' },
  closeBtn: {
    background: 'rgba(255,255,255,.18)', border: '1px solid rgba(255,255,255,.35)',
    color: '#fff', padding: '6px 14px', borderRadius: 50,
    fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  },
  closeBtnBottom: {
    background: 'linear-gradient(135deg,#185FA5,#0C447C)',
    color: '#fff', border: 'none',
    padding: '11px 28px', borderRadius: 50,
    fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
    boxShadow: '0 4px 14px rgba(24,95,165,.3)',
  },
  detailSection: { padding: '18px 22px 0' },
  cardTitle: {
    fontSize: 12, fontWeight: 800, color: '#0C447C',
    textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 14,
  },

  /* Stepper in detail */
  stepsTrack: { position: 'relative', height: 4, background: '#B5D4F4', borderRadius: 4, marginBottom: 14 },
  stepsFill:  { position: 'absolute', left: 0, top: 0, height: '100%', background: 'linear-gradient(90deg,#185FA5,#42A5F5)', borderRadius: 4, transition: 'width .6s ease' },
  stepsRow:   { display: 'flex', justifyContent: 'space-between' },
  stepCol:    { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 },
  stepDot: {
    width: 28, height: 28, borderRadius: '50%',
    border: '2px solid #B5D4F4', background: '#f7fbff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 10, fontWeight: 800, color: '#aaa', marginBottom: 6,
  },
  stepDotDone:   { background: '#E6F1FB', borderColor: '#185FA5', color: '#185FA5' },
  stepDotActive: { background: '#F57C00', borderColor: '#F57C00', color: '#fff', animation: 'ty-pulse 1.4s infinite' },
  stepLbl:       { fontSize: 9, fontWeight: 600, color: '#aaa', textAlign: 'center', lineHeight: 1.3 },

  /* Items in detail */
  itemRow:  { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '0.5px solid #e8f0fb' },
  itemIcon: { width: 38, height: 38, borderRadius: 8, background: '#E6F1FB', color: '#185FA5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, flexShrink: 0, border: '0.5px solid #B5D4F4' },
  itemInfo: { flex: 1, minWidth: 0 },
  itemName: { fontSize: 13, fontWeight: 600, color: '#1a1a2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  itemMeta: { fontSize: 11, color: '#999', marginTop: 2 },
  itemPrice:{ fontSize: 13, fontWeight: 700, color: '#F57C00', whiteSpace: 'nowrap' },

  /* Summary */
  summaryBox:    { background: '#F7FBFF', borderRadius: 10, padding: '4px 14px', border: '0.5px solid #B5D4F4' },
  summaryRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', fontSize: 13, borderBottom: '0.5px solid #e8f0fb' },
  sumLabel:      { color: '#555' },
  sumValue:      { fontWeight: 600, color: '#1a1a2e' },
  freeBadge:     { fontSize: 11, fontWeight: 700, color: '#3B6D11', background: '#EAF3DE', padding: '2px 10px', borderRadius: 20 },
  dividerDash:   { border: 'none', borderTop: '1px dashed #B5D4F4', margin: '4px 0' },
  sumTotalLabel: { fontSize: 14, fontWeight: 700, color: '#1a1a2e' },
  sumTotalValue: { fontSize: 17, fontWeight: 800, color: '#185FA5' },

  /* Info grid */
  infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  infoCell: { background: '#F7FBFF', border: '0.5px solid #B5D4F4', borderRadius: 10, padding: '11px 14px' },
  infoLabel:{ fontSize: 11, color: '#888', fontWeight: 500, marginBottom: 4 },
  infoValue: { fontSize: 13, color: '#1a1a2e', fontWeight: 600, wordBreak: 'break-all' },

  /* Empty / center */
  centerPage: { minHeight: '70vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 40 },
  emptyCard:  { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 24px', background: '#fff', borderRadius: 16, border: '0.5px solid #B5D4F4', textAlign: 'center', boxShadow: '0 2px 12px rgba(24,95,165,.07)' },
  emptyIcon:  { fontSize: 48, marginBottom: 14 },
  emptyH:     { fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 6 },
  emptyP:     { fontSize: 13, color: '#888', marginBottom: 22, lineHeight: 1.5 },
  primaryBtn: { display: 'inline-block', padding: '11px 28px', background: 'linear-gradient(135deg,#185FA5,#0C447C)', color: '#fff', borderRadius: 50, fontSize: 13, fontWeight: 700, textDecoration: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(24,95,165,.3)' },

  /* Spinner */
  spinner:    { width: 38, height: 38, borderRadius: '50%', border: '3px solid #B5D4F4', borderTopColor: '#185FA5', animation: 'spin .7s linear infinite', marginBottom: 12 },
  spinnerTxt: { fontSize: 13, color: '#888', fontWeight: 500 },

  /* Footer */
  logoBar: { maxWidth: 640, margin: '28px auto 0', textAlign: 'center', fontSize: 12, color: '#aaa', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
  logoK:   { width: 20, height: 20, background: '#F57C00', borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 800 },
};

export default MyOrders;